/**
 * Utility Function: querySelector() method shorthand
 *
 * @param {string} selector - A string containing one or more selectors to match
 * @param {object} scope - Definable scope for specific elements/objects, fallbacks to document.
 * @returns {element} returns the first Element within the document/scope that matches the specified selector, or group of selectors.
 */
export const select = (selector, scope) => {
    return (scope || document).querySelector(selector)
}


/**
 * Utility Function: querySelectorAll() method shorthand
 *
 * @param {string} selector - A string containing one or more selectors to match
 * @param {object} scope - Definable scope for specific elements/objects, fallbacks to document.
 * @returns {element} returns a list of Elements within the document/scope that matches the specified selector, or group of selectors.
 */
export const selectAll = (selector, scope) => {
    return (scope || document).querySelectorAll(selector)
}


/**
 * Checks if a given element is present in a list of elements.
 *
 * @param {HTMLElement} element - The element to check.
 * @param {HTMLElement[]} list - The list of elements to check against.
 * @returns {boolean} - Returns true if the element is in the list, false otherwise.
 */
export const isInList = (element, list) => list.includes(element);


/**
 * Utility Function: Checks if an element contains any of the specified classes.
 *
 * @param {HTMLElement} element - The DOM element to check for class names.
 * @param {string[]} classArray - An array of class names to check against the element's class list.
 * @returns {boolean} - Returns `true` if the element contains at least one of the classes in the array, otherwise `false`.
 */
export const hasAnyClass = (element, classArray) => {
    return classArray.some(cls => element.classList.contains(cls)); // cls used in place of class as it is a protected namespace
}


/**
 * Utility Function: addEventListener() method shorthand
 *
 * @param {object} target - Target to bind event listener to
 * @param {type} event - The event type(s) to listen for
 * @param {function} callback - Can be specified as either a callback function or an object whose handleEvent() method serves as the callback function.
 * @param {boolean} capture - Indicate if event of this type will be dispatched to the registered listener before being dispatched to any EventTarget, defaults to false
 */
export const listen = (target, event, callback, capture = false) => {
    target.addEventListener(event, callback, !!capture)
}


/**
 * Utility Function: Debounce function to delay function calls until events have stopped firing
 * @param {function} func Supplied function to be called after last event trigger
 * @param {milliseconds} delay Optional: Supplied time in milliseconds to wait after last event is called. Default: 250ms
 * @returns runs supplied function on debounced set timeout
 */
export function debounce(func, delay) {
    let timer = delay || 150;

    return (...args) => {
        clearTimeout(timer);

        timer = setTimeout(() => {func.apply(this, args)}, delay)
    }
}


/**
 * Utility Function: Throttle function to delay function calls until time has passed
 * @param {function} func Supplied function to be called after last event trigger
 * @param {milliseconds} delay Optional: Supplied time in milliseconds to wait until next event is called. Default: 250ms
 * @returns runs supplied function once delayed time has passed
 */
export function throttle(func, delay) {
    let timeoutHandler = false;

    return (...args) => {
        if (!timeoutHandler) {
            timeoutHandler = setTimeout(() => {
                func.apply(this, args)

                timeoutHandler = false
            }, delay);
        }
    }
}


/**
* Utility Function: Detect if element is in viewport
* @param element Supplied element to check if is in viewport
* @param offset Offset detection in pixels
* @returns {bool} returns true if element is in view
**/
export const isInViewport = (element, offset = 0) => {
    let boundingRect = element.getBoundingClientRect();

    return (boundingRect.top >= 0 && boundingRect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + offset);
}


/**
* Utility Function: Detect if element is scrollable
* @param element Supplied element to check if is currently scrollable
* @param includeHorizontalCheck (Bool) Used to also check whether the horizontal axis is also scrollable
* @returns (bool)
**/
export const isScrollable = (element, includeHorizontalCheck = false) => {
    if (includeHorizontalCheck)
        return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight

    return element.scrollHeight > element.clientHeight;
}


/**
 * Utility Function: Get largest height from supplied elements
 *
 * @param {NodeList | HTMLCollection} elements - Collection of elements to check
 * @returns {number} largest height found
 */
export const getLargestHeight = (elements) => {
    return Math.max(...Array.from(elements, element => element.getBoundingClientRect().height));
}


/**
 * Utility Function: Get parameters by name from url
 *
 * @param {string} paramName - string containing parameters to query
 * @param {string} url - string containing parameters to query
 * @returns {string} null or empty string if no results are found, otherwise returns decoded URI value(s)
 *
 * @example
    const PARAM_TO_EXTRACT = 'paramTwo'
    const URL = 'https://www.example.com/?paramOne=one&paramTwo=Hello+World'
    console.log(getParameterValue(PARAM_TO_EXTRACT, URL)) // Hello World
 */
export const getParameterValue = (paramName, url) => {
    if(!url) url = window.location.href
    const regex = new RegExp(`[?&]${paramName}(=([^&#]*))`)
    const results = regex.exec(url)

    if (!results) return null
    if (!results[2]) return ""

    return decodeURIComponent(results[2].replace(/\+/g, " "))
}


/**
 * Utility Function: Adds browser-specific classes (Safari/Firefox/Chrome/Edge) to the body element
 *
 * @returns {void} Adds 'is-safari', 'is-firefox', 'is-chrome', or 'is-edge' class to the body based on browser type
 */
export const checkBrowserType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const browserChecks = {
        'is-safari': () => userAgent.includes('safari') && !userAgent.includes('chrome'),
        'is-firefox': () => userAgent.includes('firefox'),
        'is-chrome': () => userAgent.includes('chrome') && !userAgent.includes('edge'),
        'is-edge': () => userAgent.includes('edg') // Edge uses 'edg' in its user agent string
    };

    Object.entries(browserChecks).forEach(([className, testBrowser]) => {
        if (testBrowser()) document.body.classList.add(className);
    });
};


/**
 * Utility Function: Check for iOS device
 *
 * @returns {bool} returns true if iOS platform detected
 */
export const isIOS = () => {
    return (
        (/iPad|iPhone|iPod/.test(navigator.platform) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) && !window.MSStream) //MSStream is to avoid IE11
    )
}


/**
 * Utility Function: Check for Mobile device
 *
 * @returns {bool} returns true if Mobile device detected
 * @example
 *
    if(isMobile){
        $('.c-grid-filter--media .c-grid-filter__filter__label').on('click', function() {
            $(this).parent().toggleClass('is-active');
        })
    }
*/
// let isMobileDevice = window.matchMedia("only screen and (max-width: 760px)").matches;
export const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);


/**
 * Utility Function: Checks for Mobile style breakpoints
 *
 * @returns {boolean} returns true if visually hidden element is :visible device detected
 */
export const isMobileBreakpoint = () => {
    const mobileIndicator = document.querySelector('#c-mobile-indicator');
    if (!mobileIndicator) return false;

    const style = getComputedStyle(mobileIndicator);
    const isVisible = style.display !== 'none' // && style.visibility !== 'hidden' && style.opacity !== '0';

    return window.mobileDisplay = isVisible;
}

/**
 * Utility Function: Gets Mobile style breakpoints
 *
 * @returns {number} returns the breakpoint at which mobile becomes tablet
 */
export const getMobileBreakpoint = () => {
    const mobileIndicator = document.querySelector('#c-mobile-indicator');

    if (!mobileIndicator) return null;

    const style = getComputedStyle(mobileIndicator);
    const breakpoint = style.getPropertyValue('--breakpoint').trim();

    return breakpoint ? parseInt(breakpoint, 10) : 0;
}


/**
 * Utility Function: createElement() method shorthand
 *
 * @param {string} tag - A string that specifies the type of element to be created
 * @param {string} className - A string that specifies the classes to assign to new element
 * @returns {element} returns the first Element within the document/scope that matches the specified selector, or group of selectors.
 */
export const createElement = (tag, className) => {
    const el = document.createElement(tag)
    if (className) el.classList.add(className)
    return el
}


/**
 * Utility Function: Sanatize supplied string
 *
 * @param {string} inputValue - String to be sanatized
 * @returns {element} element container sanatized string innerHTML
 */
export const sanatizeInput = (inputValue) => {
    const div = document.createElement('div')
    div.textContent = inputValue
    return div.innerHTML
}

/**
 * Utility Function: Disable sticky position based on conditions
 * @param {HTMLElement} element The element that should have its position modified
 * @param {number} offset An optional offset value to add to the viewport height (default is 0)
 * @returns {void} Removes the sticky position if conditions are met
 **/
export const disableSticky = (element, offset = 0) => {
    // Store the original position if it's not already saved
    if (!element.hasAttribute('data-original-position')) {
        element.setAttribute('data-original-position', element.style.position || '');
    }

    if (window.mobileDisplay) {
        // Mobile display is active, restore the original position
        element.style.position = element.getAttribute('data-original-position');
        return;
    }

    const viewportHeight = window.innerHeight - offset; // Subtract offset to viewport height
    const announcementHeaderBanner = document.querySelector('.c-announcement-header-banner');
    const pageHeader = document.querySelector('.c-page-header');
    const offsetHeight = (announcementHeaderBanner ? announcementHeaderBanner.offsetHeight : 0) + (pageHeader ? pageHeader.offsetHeight : 0);

    if (element.offsetHeight > viewportHeight - offsetHeight) {
        // Disable sticky position by setting to static
        element.style.position = 'static';
    } else {
        // Restore the original position
        element.style.position = element.getAttribute('data-original-position');
    }
};

/**
 * Utility Function: Flash a class on an element for a set duration
 *
 * @param {HTMLElement} element - The DOM element to apply the class to
 * @param {string} className - The CSS class to add temporarily
 * @param {number} [duration=1000] - Duration in ms before the class is removed
 */
export const flashClass = (element, className, duration = 1000) => {
  if (!(element instanceof HTMLElement)) {
    throw new Error('flashClass: element must be a valid HTMLElement')
  }

  element.classList.add(className)
  setTimeout(() => {
    element.classList.remove(className)
  }, duration)
}

